import { launchColors, phasePalette } from './launchTheme';
import { ActivityAction, ActivityProjection, LaunchStatus, ScheduleStatus, WorkStatus } from './types';

export const launchStatusText: Record<LaunchStatus, string> = {
  draft: 'Utkast',
  active: 'Aktiv',
  paused: 'Pausad',
  launched: 'Lanserad',
  closed: 'Avslutad',
  cancelled: 'Avbruten',
};

export const workStatusText: Record<WorkStatus, string> = {
  not_started: 'Inte startad',
  in_progress: 'Pågår',
  in_review: 'Väntar på granskning',
  changes_requested: 'Ändringar begärda',
  done: 'Klar',
};

export const scheduleStatusText: Record<ScheduleStatus, string> = {
  complete: 'Klar',
  overdue: 'Försenad',
  at_risk: 'Riskerar att bli sen',
  due_soon: 'Deadline snart',
  on_track: 'Enligt plan',
  missing_due: 'Datum saknas',
};

const eventLabels: Record<string, string> = {
  start: 'Aktivitet startad',
  assign: 'Ansvarig ändrad',
  complete: 'Aktivitet markerad klar',
  reopen: 'Aktivitet återöppnad',
  blocker: 'Hinder registrerat',
  resolveBlocker: 'Hinder löst',
  decide: 'Beslut registrerat',
  revise: 'Underlag reviderat',
  updateMetadata: 'Lanseringsuppgifter ändrade',
  updateRoleUsers: 'Roller ändrade',
  changeChains: 'Kedjor ändrade',
  activate: 'Lansering aktiverad',
  pause: 'Lansering pausad',
  resume: 'Lansering återupptagen',
  markLaunched: 'Lansering markerad lanserad',
  close: 'Lansering avslutad',
  cancel: 'Lansering avbruten',
  setForecast: 'Prognos uppdaterad',
  recordLateChange: 'Sen ändring registrerad',
  proposePlanChange: 'Planändring föreslagen',
  commitPlanChange: 'Planändring fastställd',
  captureWeeklySnapshot: 'Veckoobservation sparad',
  correctWeeklySnapshot: 'Veckoobservation rättad',
  attachFile: 'Fil bifogad',
  removeFile: 'Fil borttagen',
  moveFile: 'Fil flyttad',
  createFileFolder: 'Mapp skapad',
  renameFileFolder: 'Mapp omdöpt',
  removeFileFolder: 'Mapp borttagen',
  applyTemplateNames: 'Uppdaterad från mallen',
};

export const eventTypeText = (type: string): string => eventLabels[type] || 'Lanseringen uppdaterades';

export const eventDetail = (event: { comment?: string; reason?: string; metadata?: Record<string, unknown> }): string => {
  const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  const fileName = text(event.metadata?.fileName);
  const folderName = text(event.metadata?.folderName);
  const previousFolderName = text(event.metadata?.previousFolderName);
  const previousName = text(event.metadata?.previousName);
  const parts: string[] = [];
  if (fileName && previousFolderName && folderName) parts.push(`${fileName} · ${previousFolderName} → ${folderName}`);
  else if (fileName) parts.push(folderName ? `${fileName} · ${folderName}` : fileName);
  else if (previousName && folderName) parts.push(`${previousName} → ${folderName}`);
  else if (folderName) parts.push(folderName);
  const changes = event.metadata?.changes;
  const roleChanges = Array.isArray(changes)
    ? changes.flatMap((change) => {
      if (!change || typeof change !== 'object') return [];
      const role = text((change as { role?: unknown }).role);
      const count = (change as { reassignedActivityIds?: unknown }).reassignedActivityIds;
      return role && Array.isArray(count) ? [`${role}: ${count.length} uppgifter`] : [];
    })
    : [];
  if (roleChanges.length) parts.push(roleChanges.join(', '));
  const summary = event.metadata?.summary;
  if (summary && typeof summary === 'object' && typeof (summary as { text?: unknown }).text === 'string') {
    parts.push((summary as { text: string }).text);
  }
  const note = event.comment || event.reason || '';
  if (note) parts.push(note);
  return parts.join(' · ');
};

export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 1024) return `${Math.max(0, Math.round(bytes || 0))} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes >= 10 ? Math.round(megabytes) : megabytes.toFixed(1)} MB`;
};

export const saveDownloadedFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const today = (): string => new Date().toISOString().slice(0, 10);

export const makeId = (prefix: string): string => {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`;
};

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return 'Datum saknas';
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
};

export type MyWorkGroup = 'Försenat' | 'Behöver åtgärd' | 'Idag' | 'Kommande' | 'Utan datum' | 'Blockerat' | 'Väntar';
export const myWorkGroupOrder: MyWorkGroup[] = ['Försenat', 'Behöver åtgärd', 'Idag', 'Kommande', 'Utan datum', 'Blockerat', 'Väntar'];

export const myWorkGroup = (activity: ActivityProjection, asOf: string): MyWorkGroup => {
  if (activity.activeBlocker) return 'Blockerat';
  if (activity.workStatus === 'in_review') return 'Väntar';
  if (activity.scheduleStatus === 'overdue') return 'Försenat';
  if (activity.due === asOf) return 'Idag';
  if (activity.scheduleStatus === 'missing_due' && !activity.actionRequired) return 'Utan datum';
  if (activity.actionRequired) return 'Behöver åtgärd';
  return 'Kommande';
};

export const compareActivities = (left: ActivityProjection, right: ActivityProjection): number => {
  const rank: Record<ScheduleStatus, number> = {
    overdue: 0,
    at_risk: 1,
    due_soon: 2,
    missing_due: 3,
    on_track: 4,
    complete: 5,
  };
  return rank[left.scheduleStatus] - rank[right.scheduleStatus]
    || String(left.due || '9999-12-31').localeCompare(String(right.due || '9999-12-31'))
    || left.title.localeCompare(right.title, 'sv');
};

export const readStoredJson = <T,>(key: string, fallback: T): T => {
  try {
    const value = localStorage.getItem(key);
    return value ? { ...fallback, ...JSON.parse(value) } : fallback;
  } catch {
    return fallback;
  }
};

// Versala ord i källarbetsboken som ska behålla sin form när texten skrivs om till normal skrivning.
const preservedWords: Record<string, string> = {
  AD: 'AD',
  ATHENA: 'Athena',
  AXFOOD: 'Axfood',
  COOP: 'Coop',
  DABAS: 'Dabas',
  DL: 'DL',
  FONTANA: 'Fontana',
  HOI: 'HoI',
  ICA: 'ICA',
  INHOUSE: 'Inhouse',
  KAM: 'KAM',
  NIELSEN: 'Nielsen',
  POS: 'POS',
  QLAB: 'QLab',
  ROI: 'ROI',
  SC: 'SC',
  VALIDOO: 'Validoo',
  VCD: 'VCD',
};

const isUpperWord = (word: string): boolean => word.length > 1
  && word === word.toLocaleUpperCase('sv')
  && word !== word.toLocaleLowerCase('sv');

export const displayText = (value: string | null | undefined): string => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  const words = text.match(/\p{L}+/gu) || [];
  if (!words.some((word) => word.length >= 3 && isUpperWord(word) && !preservedWords[word])) return text;
  let segmentStart = true;
  return text.replace(/\p{L}+|[^\p{L}]+/gu, (token) => {
    if (!/\p{L}/u.test(token)) {
      if (/\s-\s/.test(token) || (/\/\s*$/.test(token) && !/-\/\s*$/.test(token))) segmentStart = true;
      return token.replace(/\s-\s/g, ' – ');
    }
    const startsSegment = segmentStart;
    segmentStart = false;
    if (!isUpperWord(token)) return token;
    const preserved = preservedWords[token];
    if (preserved) return preserved;
    const lower = token.toLocaleLowerCase('sv');
    return startsSegment ? lower.charAt(0).toLocaleUpperCase('sv') + lower.slice(1) : lower;
  });
};

const dayMs = 24 * 60 * 60 * 1000;
const utcDay = (value: string): number => Date.UTC(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));

export const diffDays = (later: string, earlier: string): number => Math.round((utcDay(later) - utcDay(earlier)) / dayMs);

export const addDays = (value: string, days: number): string => new Date(utcDay(value) + days * dayMs).toISOString().slice(0, 10);

export const isoWeek = (value: string): number => {
  const date = new Date(utcDay(value));
  const weekday = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - weekday);
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - yearStart) / dayMs + 1) / 7);
};

export const mondayOf = (value: string): string => {
  const weekday = new Date(utcDay(value)).getUTCDay() || 7;
  return addDays(value, 1 - weekday);
};

export const monthNames = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export const shortDate = (value: string | null | undefined, referenceDate?: string): string => {
  if (!value) return 'Datum saknas';
  const label = `${Number(value.slice(8, 10))} ${monthNames[Number(value.slice(5, 7)) - 1] || ''}`;
  return referenceDate && referenceDate.slice(0, 4) !== value.slice(0, 4) ? `${label} ${value.slice(0, 4)}` : label;
};

export const relativeDue = (due: string | null | undefined, asOf: string): string => {
  if (!due) return 'datum saknas';
  const days = diffDays(due.slice(0, 10), asOf);
  if (days === 0) return 'idag';
  if (days === 1) return 'i morgon';
  if (days === -1) return '1 d sen';
  return days > 0 ? `om ${days} d` : `${-days} d sen`;
};

export const countdownText = (date: string, asOf: string): string => {
  const days = diffDays(date, asOf);
  if (days === 0) return 'idag';
  if (days > 0) return `om ${days} ${days === 1 ? 'dag' : 'dagar'}`;
  return `för ${-days} ${days === -1 ? 'dag' : 'dagar'} sedan`;
};

export type Severity = 'blocked' | 'late' | 'risk' | 'soon' | 'missing' | 'ok' | 'done';

export const severityOf = (activity: Pick<ActivityProjection, 'activeBlocker' | 'scheduleStatus'>): Severity => {
  if (activity.scheduleStatus === 'complete') return 'done';
  if (activity.activeBlocker) return 'blocked';
  if (activity.scheduleStatus === 'overdue') return 'late';
  if (activity.scheduleStatus === 'at_risk') return 'risk';
  if (activity.scheduleStatus === 'due_soon') return 'soon';
  if (activity.scheduleStatus === 'missing_due') return 'missing';
  return 'ok';
};

export const phaseColor = (phaseKey: string | null | undefined): string => {
  const key = String(phaseKey || '');
  if (key.startsWith('custom-phase')) return launchColors.textMuted;
  const numbered = /(\d+)\s*$/.exec(key);
  if (numbered) return phasePalette[(Number(numbered[1]) - 1 + phasePalette.length) % phasePalette.length];
  const hash = Array.from(key).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return phasePalette[hash % phasePalette.length];
};

export const isDueThisWeek = (activity: Pick<ActivityProjection, 'due' | 'scheduleStatus'>, asOf: string): boolean => {
  if (!activity.due || activity.scheduleStatus === 'complete') return false;
  return activity.due >= asOf && activity.due <= addDays(mondayOf(asOf), 6);
};

export const initials = (name: string): string => name
  .split(/[\s-]+/)
  .filter((part) => /\p{L}/u.test(part))
  .map((part) => part.match(/\p{L}/u)?.[0] || '')
  .slice(0, 2)
  .join('')
  .toLocaleUpperCase('sv') || '?';

export const primaryActivityAction = (
  activity: Pick<ActivityProjection, 'allowedActions' | 'activeBlocker' | 'kind' | 'workStatus'>,
  canApprove: boolean,
): ActivityAction | null => {
  const allowed = new Set(activity.allowedActions.filter((action) => action !== 'decide' || canApprove));
  if (activity.activeBlocker && allowed.has('resolveBlocker')) return 'resolveBlocker';
  if (allowed.has('decide') && (activity.kind !== 'work' || activity.workStatus === 'in_review')) return 'decide';
  if (allowed.has('start')) return 'start';
  if (allowed.has('complete')) return 'complete';
  if (allowed.has('decide')) return 'decide';
  return null;
};
